import React, { memo } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { AutocompleteCDS, CheckboxCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import {
  contributingFileNamesStyles,
  editedBadgeStyles,
  getSplitPageChipStyles,
  groupingChipRowStyles,
  groupingChipStyles,
  mergeButtonStyles,
  previewButtonStyles,
  reclassifyButtonStyles,
  removeButtonStyles,
  restoreButtonStyles,
  segmentActionBarStyles,
  segmentConfidenceBadgeHighStyles,
  segmentConfidenceBadgeLowStyles,
  segmentConfidenceBadgeNeutralStyles,
  segmentInfoCellStyles,
  segmentNameTypographyStyles,
  segmentPreviewRowStyles,
  segmentRemovedRowStyles,
  segmentRowLowConfidenceStyles,
  segmentRowStyles,
  segmentTypeSelectorStyles,
  segmentUnrecognizedStyles,
  splitButtonStyles,
  splitPickerBannerStyles,
  splitPickerPageStyles,
} from '../AiBatchIntake.styles';
import {
  formatConfidencePercent,
  formatEditedBadge,
  formatGroupingIndicator,
  formatSegmentLocation,
  getContributingFileNames,
  isGroupedSegment,
  isSegmentLowConfidence,
} from '../AiBatchIntake.utils';
import IntakeDuplicateBadge from '../IntakeDuplicateBadge';
import IntakeGroupedSegmentPreview from '../IntakeGroupedSegmentPreview';
import IntakeSegmentPreview from '../IntakeSegmentPreview';

import { IntakeSegmentRowTestIds } from './IntakeSegmentRow.testIds';

/**
 * Derive confidence badge sx for a segment.
 *
 * @param {boolean} isNull
 * @param {boolean} isLow
 * @returns {object}
 */
const deriveConfidenceBadgeSx = (isNull, isLow) => {
  if (isNull) {
    return segmentConfidenceBadgeNeutralStyles;
  }

  if (isLow) {
    return segmentConfidenceBadgeLowStyles;
  }

  return segmentConfidenceBadgeHighStyles;
};

/**
 * Renders a single intake segment row in the review drawer.
 *
 * Handles both the removed-state (muted row + Restore button) and the active
 * state (row + optional preview rail + action bar + split picker).
 *
 * All mutation callbacks are lifted to AiBatchIntake; this component is pure display.
 *
 * DEV-4555 / DEV-4560 / DEV-4561.
 */
const IntakeSegmentRow = ({
  seg,
  documentTypeOptions,
  hasPrev,
  hasNext,
  splitPickerSegId,
  splitPickerPage,
  previewOpenSegId,
  reclassifyingSegIds,
  isBackgroundReview,
  onLoadSource,
  onToggleSegment,
  onSegmentTypeChange,
  onTogglePreview,
  onOpenSplitPicker,
  onSplitPageSelect,
  onApplySplit,
  onCancelSplit,
  onMergeWithPrev,
  onMergeWithNext,
  onRemoveSegment,
  onRestoreSegment,
  onReclassify,
}) => {
  const typeOption = documentTypeOptions.find(o => o.value === seg.documentTypeId) ?? null;
  const editedBadge = formatEditedBadge(seg);
  const isReclassifying = reclassifyingSegIds.has(seg.id);
  const isPreviewOpen = previewOpenSegId === seg.id;
  const isSplitPickerOpen = splitPickerSegId === seg.id;
  const canSplit = seg.pageEnd > seg.pageStart;

  // Build split-picker page list: pages from pageStart+1 to pageEnd.
  const splitPickerPages = Array.from(
    { length: seg.pageEnd - seg.pageStart },
    (_, i) => seg.pageStart + 1 + i,
  );

  if (seg.isRemoved) {
    return (
      <Box sx={segmentRemovedRowStyles}>
        <Box sx={segmentInfoCellStyles}>
          <Typography color="text.secondary" variant="regular12">
            {formatSegmentLocation(seg)}
          </Typography>
          <Typography color="text.secondary" variant="medium14">
            {typeOption?.name ?? 'Removed'}
          </Typography>
        </Box>
        <Button
          data-testid={IntakeSegmentRowTestIds.getRestoreButtonId(seg.id)}
          size="small"
          sx={restoreButtonStyles}
          variant="outlined"
          onClick={onRestoreSegment}
        >
          Restore
        </Button>
      </Box>
    );
  }

  const isNullConfidence = seg.classifyConfidence == null;
  const low = isSegmentLowConfidence(seg.classifyConfidence);
  const rowSx = low ? segmentRowLowConfidenceStyles : segmentRowStyles;
  const confidenceSx = deriveConfidenceBadgeSx(isNullConfidence, low);
  const isGrouped = isGroupedSegment(seg);
  const contributingFileNames = isGrouped ? getContributingFileNames(seg).join(' · ') : null;
  const segmentName = typeOption?.name ?? null;

  return (
    <>
      <Box sx={rowSx}>
        <CheckboxCDS
          dataTestId={IntakeSegmentRowTestIds.getSelectCheckboxId(seg.id)}
          value={seg.isSelected}
          onChange={onToggleSegment(seg.id)}
        />

        <Box sx={segmentInfoCellStyles}>
          <Typography color="text.secondary" variant="regular12">
            {formatSegmentLocation(seg)}
          </Typography>
          <Typography sx={segmentNameTypographyStyles} variant="medium14">
            {typeOption?.name ?? (
              <Typography component="span" sx={segmentUnrecognizedStyles} variant="medium14">
                Unrecognized
              </Typography>
            )}
          </Typography>
          {editedBadge && (
            <Box component="span" sx={editedBadgeStyles}>
              <Typography variant="regular12">{editedBadge}</Typography>
            </Box>
          )}
          {isGrouped && (
            <Box sx={groupingChipRowStyles}>
              <Box
                data-testid={IntakeSegmentRowTestIds.getGroupingChipId(seg.id)}
                sx={groupingChipStyles}
              >
                <Typography variant="regular12">{formatGroupingIndicator(seg)}</Typography>
              </Box>
              <Typography
                color="text.secondary"
                sx={contributingFileNamesStyles}
                variant="regular12"
              >
                {contributingFileNames}
              </Typography>
            </Box>
          )}
          {seg.duplicateOf != null && (
            <IntakeDuplicateBadge
              duplicateOf={seg.duplicateOf}
              isSelected={!!seg.isSelected}
              segId={seg.id}
            />
          )}
        </Box>

        <Box sx={segmentTypeSelectorStyles}>
          <AutocompleteCDS
            dataTestId={IntakeSegmentRowTestIds.getTypeSelectId(seg.id)}
            label="Document type"
            name={seg.id}
            options={documentTypeOptions}
            value={typeOption}
            onChange={onSegmentTypeChange}
          />
        </Box>

        <Box
          aria-label={`AI confidence: ${formatConfidencePercent(seg.classifyConfidence)}`}
          sx={confidenceSx}
        >
          <Typography variant="regular12">
            {formatConfidencePercent(seg.classifyConfidence)}
          </Typography>
        </Box>
      </Box>

      {isBackgroundReview && (
        <>
          {isPreviewOpen && (
            <Box sx={segmentPreviewRowStyles}>
              {isGrouped ? (
                <IntakeGroupedSegmentPreview
                  segment={seg}
                  segmentName={segmentName}
                  onLoadSource={onLoadSource}
                />
              ) : (
                <IntakeSegmentPreview
                  segment={seg}
                  segmentName={segmentName}
                  onLoadSource={onLoadSource}
                />
              )}
            </Box>
          )}

          <Box sx={segmentActionBarStyles}>
            <Button
              data-testid={IntakeSegmentRowTestIds.getPreviewButtonId(seg.id)}
              size="small"
              sx={previewButtonStyles}
              variant="outlined"
              onClick={onTogglePreview}
            >
              {isPreviewOpen ? '▾ Hide preview' : '◉ Preview'}
            </Button>

            {canSplit && (
              <Button
                data-testid={IntakeSegmentRowTestIds.getSplitButtonId(seg.id)}
                size="small"
                sx={splitButtonStyles}
                variant="outlined"
                onClick={onOpenSplitPicker}
              >
                ✂ Split here
              </Button>
            )}

            {hasPrev && (
              <Button
                data-testid={IntakeSegmentRowTestIds.getMergePrevButtonId(seg.id)}
                size="small"
                sx={mergeButtonStyles}
                variant="outlined"
                onClick={onMergeWithPrev}
              >
                ← Merge with prev
              </Button>
            )}

            {hasNext && (
              <Button
                data-testid={IntakeSegmentRowTestIds.getMergeNextButtonId(seg.id)}
                size="small"
                sx={mergeButtonStyles}
                variant="outlined"
                onClick={onMergeWithNext}
              >
                → Merge with next
              </Button>
            )}

            <Button
              data-testid={IntakeSegmentRowTestIds.getRemoveButtonId(seg.id)}
              size="small"
              sx={removeButtonStyles}
              variant="outlined"
              onClick={onRemoveSegment}
            >
              ✕ Remove
            </Button>

            <Button
              data-testid={IntakeSegmentRowTestIds.getReclassifyButtonId(seg.id)}
              disabled={isReclassifying}
              size="small"
              sx={reclassifyButtonStyles}
              variant="outlined"
              onClick={onReclassify}
            >
              {isReclassifying ? 'Re-classifying…' : '↺ Re-classify'}
            </Button>
          </Box>

          {isSplitPickerOpen && canSplit && (
            <Box sx={splitPickerBannerStyles}>
              <Typography color="text.secondary" variant="regular12">
                Cut before page:
              </Typography>
              <Box sx={splitPickerPageStyles}>
                {splitPickerPages.map(p => (
                  <Box
                    key={p}
                    sx={getSplitPageChipStyles(splitPickerPage === p)}
                    onClick={onSplitPageSelect(p)}
                  >
                    <Typography variant="regular12">{p}</Typography>
                  </Box>
                ))}
              </Box>
              <Button
                data-testid={IntakeSegmentRowTestIds.getApplySplitButtonId(seg.id)}
                disabled={splitPickerPage == null}
                size="small"
                variant="contained"
                onClick={onApplySplit}
              >
                Apply
              </Button>
              <Button
                data-testid={IntakeSegmentRowTestIds.getCancelSplitButtonId(seg.id)}
                size="small"
                variant="outlined"
                onClick={onCancelSplit}
              >
                Cancel
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );
};

IntakeSegmentRow.propTypes = {
  seg: PropTypes.shape({
    id: PropTypes.string.isRequired,
    pageStart: PropTypes.number.isRequired,
    pageEnd: PropTypes.number.isRequired,
    isRemoved: PropTypes.bool,
    isEdited: PropTypes.bool,
    isSelected: PropTypes.bool,
    documentTypeId: PropTypes.string,
    classifyConfidence: PropTypes.number,
    jobId: PropTypes.string,
    sourceIndex: PropTypes.number,
    sourceFileName: PropTypes.string,
    truncated: PropTypes.bool,
    parts: PropTypes.arrayOf(
      PropTypes.shape({
        sourceIndex: PropTypes.number,
        pageIndex: PropTypes.number,
        sourceFileName: PropTypes.string,
      }),
    ),
    duplicateOf: PropTypes.shape({
      jobId: PropTypes.string,
      documentId: PropTypes.string,
      filedDate: PropTypes.string,
    }),
  }).isRequired,
  documentTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string, name: PropTypes.string }),
  ).isRequired,
  hasNext: PropTypes.bool.isRequired,
  hasPrev: PropTypes.bool.isRequired,
  isBackgroundReview: PropTypes.bool.isRequired,
  reclassifyingSegIds: PropTypes.instanceOf(Set).isRequired,
  onApplySplit: PropTypes.func.isRequired,
  onCancelSplit: PropTypes.func.isRequired,
  onLoadSource: PropTypes.func.isRequired,
  onMergeWithNext: PropTypes.func.isRequired,
  onMergeWithPrev: PropTypes.func.isRequired,
  onOpenSplitPicker: PropTypes.func.isRequired,
  onReclassify: PropTypes.func.isRequired,
  onRemoveSegment: PropTypes.func.isRequired,
  onRestoreSegment: PropTypes.func.isRequired,
  onSegmentTypeChange: PropTypes.func.isRequired,
  onSplitPageSelect: PropTypes.func.isRequired,
  onTogglePreview: PropTypes.func.isRequired,
  onToggleSegment: PropTypes.func.isRequired,
  previewOpenSegId: PropTypes.string,
  splitPickerPage: PropTypes.number,
  splitPickerSegId: PropTypes.string,
};

export default memo(IntakeSegmentRow);
